import { useCallback, useEffect, useRef, useState } from 'react';

import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { courtesyContentStringAtom } from '@service/bugo/containers/MemberCourtesy/courtesy.atom';
import { getCourtesyStringFromContent } from '@service/bugo/containers/MemberCourtesy/courtesyGenerator';
import { bugoDataAtomFamily, bugoStatusAtomFamily } from '@shared/api/bugo/bugo.atom';
import { useBugoHook } from '@shared/api/bugo/bugo.hook';
import {
  courtesyContentListByMemberDataAtom,
  courtesyContentListByMemberStatusAtom,
} from '@shared/api/courtesyAsset/courtesyAsset.atom';
import { useCourtesyContentListByMemberHook } from '@shared/api/courtesyAsset/courtesyAsset.hook';
import { giftOrderForStoreDataAtomFamily } from '@shared/api/giftOrder/giftOrder.atom';
import { updateGiftOrderCourtesy } from '@shared/api/giftOrder/giftOrder.controller';
import {
  GiftOrderForStore,
  GiftStoreRole,
} from '@shared/api/giftOrder/giftOrder.interface';

interface UseCourtesyEditorParams {
  orderId: string;
  order: GiftOrderForStore | null;
  role: GiftStoreRole;
}

export function useCourtesyEditor({ orderId, order, role }: UseCourtesyEditorParams) {
  const bugoId = order?.bugo ?? '';
  useBugoHook(bugoId, !bugoId);
  const bugoStatus = useRecoilValue(bugoStatusAtomFamily(bugoId));
  const bugo = useRecoilValue(bugoDataAtomFamily(bugoId));

  const memberId = bugo?.member?._id ?? '';
  useCourtesyContentListByMemberHook(memberId);
  const courtesyContentListStatus = useRecoilValue(courtesyContentListByMemberStatusAtom);
  const courtesyContentList = useRecoilValue(courtesyContentListByMemberDataAtom);

  const [savedCourtesyString, setSavedCourtesyString] = useRecoilState(
    courtesyContentStringAtom,
  );
  const setOrderData = useSetRecoilState(giftOrderForStoreDataAtomFamily(orderId));

  const [courtesyContent, setCourtesyContent] = useState('');
  const [selectedContentIndex, setSelectedContentIndex] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isModified, setIsModified] = useState(false);
  const [originalContent, setOriginalContent] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // 초기 답례글 설정 (우선순위: order.courtesyContent > savedCourtesyString > 템플릿)
  useEffect(() => {
    if (isInitialized) return;

    if (order?.courtesyContent) {
      setCourtesyContent(order.courtesyContent);
      setOriginalContent(order.courtesyContent);
      setSavedCourtesyString(order.courtesyContent);
      setIsInitialized(true);
      return;
    }

    if (savedCourtesyString) {
      setCourtesyContent(savedCourtesyString);
      setOriginalContent(savedCourtesyString);
      setIsInitialized(true);
      return;
    }

    if (bugo && courtesyContentList && courtesyContentList.length > 0) {
      const defaultContentId = bugo.fevent?.courtesyContent;
      const defaultContent = defaultContentId
        ? courtesyContentList.find((c) => c._id === defaultContentId)
        : courtesyContentList[0];

      if (defaultContent) {
        const generatedContent = getCourtesyStringFromContent(defaultContent, bugo);
        setCourtesyContent(generatedContent);
        setOriginalContent(generatedContent);
        setSavedCourtesyString(generatedContent);

        const index = courtesyContentList.findIndex((c) => c._id === defaultContent._id);
        setSelectedContentIndex(index >= 0 ? index : 0);
        setIsInitialized(true);
      }
    }
  }, [
    isInitialized,
    order?.courtesyContent,
    bugo,
    courtesyContentList,
    savedCourtesyString,
    setSavedCourtesyString,
  ]);

  const handleContentSelect = useCallback(
    (index: number) => {
      if (!courtesyContentList || !bugo) return;

      const selected = courtesyContentList[index];
      if (selected) {
        const generatedContent = getCourtesyStringFromContent(selected, bugo);
        setCourtesyContent(generatedContent);
        setSavedCourtesyString(generatedContent);
        setSelectedContentIndex(index);
        setIsModified(generatedContent !== originalContent);
      }
    },
    [courtesyContentList, bugo, setSavedCourtesyString, originalContent],
  );

  const handleContentChange = useCallback(
    (value: string) => {
      setCourtesyContent(value);
      setSavedCourtesyString(value);
      setIsModified(value !== originalContent);
    },
    [setSavedCourtesyString, originalContent],
  );

  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, []);

  useEffect(() => {
    if (isEditing) {
      adjustTextareaHeight();
    }
  }, [isEditing, courtesyContent, adjustTextareaHeight]);

  const handleSaveCourtesy = useCallback(async () => {
    if (!orderId || !courtesyContent) return;

    try {
      setIsSaving(true);
      const updatedOrder = await updateGiftOrderCourtesy(
        orderId,
        { courtesyContent },
        role,
      );
      setOrderData((prev) =>
        prev ? { ...prev, courtesyContent: updatedOrder.courtesyContent } : prev,
      );
      setOriginalContent(courtesyContent);
      setIsModified(false);
      setIsEditing(false);
      alert('답례글이 저장되었습니다.');
    } catch (error) {
      console.error('답례글 저장 실패:', error);
      alert('답례글 저장에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  }, [orderId, courtesyContent, role, setOrderData]);

  const handleCancelEdit = useCallback(() => {
    if (isModified) {
      setCourtesyContent(originalContent);
      setSavedCourtesyString(originalContent);
      setIsModified(false);
    }
    setIsEditing(false);
  }, [isModified, originalContent, setSavedCourtesyString]);

  return {
    courtesyContent,
    selectedContentIndex,
    isEditing,
    setIsEditing,
    isSaving,
    isModified,
    textareaRef,
    bugoStatus,
    courtesyContentListStatus,
    courtesyContentList,
    handleContentSelect,
    handleContentChange,
    adjustTextareaHeight,
    handleSaveCourtesy,
    handleCancelEdit,
  };
}
